/**
 * Centralised RBAC (role-based access control) helper for the frontend.
 *
 * The backend is the source of truth — every API endpoint enforces firm
 * scoping and role checks server-side. These helpers exist to:
 *
 *   1. Hide UI that the user has no permission to use.
 *   2. Prevent a user from reaching a sensitive page directly via URL
 *      (e.g. typing /accounting/general-ledger as a paralegal).
 *
 * Bypassing this layer is not a security issue — the backend will still
 * reject the request. The frontend gate is purely a UX guard.
 */

import { ROLES } from './roles';

const SUPERUSER = ROLES.SUPERUSER;
const PARTNER = ROLES.PARTNER;
const ACCOUNTANT = 'ACCOUNTANT';
const ASSOCIATE = ROLES.ASSOCIATE;
const PARALEGAL = ROLES.PARALEGAL;
const SUPPORT_STAFF = ROLES.SUPPORT_STAFF;
const BOARD_MEMBER = ROLES.BOARD_MEMBER;
const AUDITOR = 'AUDITOR';

const ALL_STAFF = [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, PARALEGAL, SUPPORT_STAFF];

/**
 * tabId → list of roles that may access it.
 * Tabs not listed here are open to all authenticated staff users.
 */
export const TAB_ACCESS = {
  // ── Accounting & finance ─ accountants + partners only ──
  chartOfAccounts:        [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  generalLedger:          [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  suppliers:              [SUPERUSER, PARTNER, ACCOUNTANT],
  creditors:              [SUPERUSER, PARTNER, ACCOUNTANT],
  apBills:                [SUPERUSER, PARTNER, ACCOUNTANT],
  pettyCash:              [SUPERUSER, PARTNER, ACCOUNTANT],
  accountingReview:       [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  firmExpenses:           [SUPERUSER, PARTNER, ACCOUNTANT],
  bankAccounts:           [SUPERUSER, PARTNER, ACCOUNTANT],
  bankReconciliation:     [SUPERUSER, PARTNER, ACCOUNTANT],
  vatReturns:             [SUPERUSER, PARTNER, ACCOUNTANT],
  trialBalance:           [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  balanceSheet:           [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  incomeStatement:        [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  cashFlow:               [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  accountingPeriods:      [SUPERUSER, PARTNER, ACCOUNTANT],
  trustAccounting:        [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],

  // ── Admin & sensitive settings ──
  manageAuditors:         [SUPERUSER],
  firmSettings:           [SUPERUSER, PARTNER],
  payrollManagement:      [SUPERUSER, PARTNER, ACCOUNTANT],
  teamOverview:           [SUPERUSER, PARTNER, BOARD_MEMBER],
  departments:            [SUPERUSER, PARTNER],
  imports:                [SUPERUSER, PARTNER],
  security:               ALL_STAFF.concat(BOARD_MEMBER),

  // ── Reports — staff who can see financials ──
  reports:                [SUPERUSER, PARTNER, ACCOUNTANT, BOARD_MEMBER, AUDITOR],
  insightsReports:        [SUPERUSER, PARTNER, ACCOUNTANT, BOARD_MEMBER],

  // ── Billing / collections ──
  billing:                [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE],
  debtCollectionDashboard: [SUPERUSER, PARTNER, ACCOUNTANT],
  collectionsSettings:    [SUPERUSER, PARTNER, ACCOUNTANT],
  billsOfCosts:           [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE],
  serviceProducts:        [SUPERUSER, PARTNER, ACCOUNTANT],

  // ── Compliance & audit ──
  auditManagement:        [SUPERUSER, PARTNER, AUDITOR],
  complianceHub:          [SUPERUSER, PARTNER, ACCOUNTANT],
  attorneyRecords:        [SUPERUSER, PARTNER, ACCOUNTANT],
  annualCertificateManagement: [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
};

/**
 * Returns true if the user role can access the given tab.
 * Superusers always get access. Unknown tabs are treated as open.
 */
export function canAccessTab(userRole, tabId, currentUser = null) {
  if (currentUser && currentUser.is_superuser) return true;
  if (userRole === SUPERUSER) return true;
  const allowed = TAB_ACCESS[tabId];
  if (!allowed) return true; // Tab not gated — open to all staff
  return allowed.includes(userRole);
}

/**
 * Get the list of tabs the current user is NOT allowed to access.
 * Useful for redirecting away from blocked URLs.
 */
export function blockedTabsFor(userRole, currentUser = null) {
  if (currentUser && currentUser.is_superuser) return [];
  if (userRole === SUPERUSER) return [];
  return Object.keys(TAB_ACCESS).filter(t => !canAccessTab(userRole, t, currentUser));
}
